import { ThemeEvents } from '@theme/events';

/**
 * Shows the current variant SKU in a tooltip when the product title is clicked.
 *
 * @example
 * ```html
 * <product-title-sku-tooltip data-sku="ABC-123">
 *   <h1>Product name</h1>
 * </product-title-sku-tooltip>
 * ```
 */
class ProductTitleSkuTooltip extends HTMLElement {
  /** @type {string} */
  #sku = '';
  /** @type {HTMLElement | null} */
  #tooltip = null;
  /** @type {((event: MouseEvent) => void) | null} */
  #onDocumentClick = null;
  /** @type {((event: KeyboardEvent) => void) | null} */
  #onKeyDown = null;
  /** @type {HTMLElement | null} */
  #titleEl = null;
  /** @type {HTMLElement | null} */
  #variantTarget = null;

  connectedCallback() {
    this.#sku = this.dataset.sku || this.#getInitialSku();
    this.#titleEl = this.querySelector('h1, h2, h3, h4, h5, h6') || this.firstElementChild;

    if (!(this.#titleEl instanceof HTMLElement)) return;

    this.#titleEl.classList.add('product-title-sku-tooltip__trigger');
    this.#titleEl.addEventListener('click', this.#onTitleClick);

    this.#variantTarget = this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"]');
    this.#variantTarget?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
  }

  #getInitialSku() {
    const details = this.closest('.product-details[id*="ProductInformation-"]');
    if (details instanceof HTMLElement) {
      return details.dataset.initialSku || '';
    }

    return '';
  }

  disconnectedCallback() {
    this.#titleEl?.removeEventListener('click', this.#onTitleClick);
    this.#variantTarget?.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    this.#hideTooltip();
  }

  #onVariantUpdate = (/** @type {CustomEvent} */ event) => {
    const resource = event.detail?.resource;
    if (!resource) return;

    this.#sku = resource.sku || '';
    this.dataset.sku = this.#sku;

    if (this.#tooltip) {
      this.#tooltip.textContent = this.#sku || '—';
    }
  };

  #onTitleClick = (/** @type {MouseEvent} */ event) => {
    event.preventDefault();
    event.stopPropagation();

    if (this.#tooltip?.classList.contains('is-visible')) {
      this.#hideTooltip();
      return;
    }

    this.#showTooltip();
  };

  #showTooltip() {
    if (!this.#titleEl) return;

    this.#tooltip = document.createElement('div');
    this.#tooltip.className = 'product-title-sku-tooltip__popup';
    this.#tooltip.setAttribute('role', 'tooltip');
    this.#tooltip.textContent = this.#sku || '—';
    document.body.appendChild(this.#tooltip);

    this.#positionTooltip();

    requestAnimationFrame(() => {
      this.#tooltip?.classList.add('is-visible');
    });

    this.#onDocumentClick = (event) => {
      const target = /** @type {Node} */ (event.target);
      if (!this.contains(target) && !this.#tooltip?.contains(target)) {
        this.#hideTooltip();
      }
    };

    this.#onKeyDown = (event) => {
      if (event.key === 'Escape') {
        this.#hideTooltip();
      }
    };

    setTimeout(() => {
      if (this.#onDocumentClick) document.addEventListener('click', this.#onDocumentClick, true);
      if (this.#onKeyDown) document.addEventListener('keydown', this.#onKeyDown);
    }, 0);
  }

  #positionTooltip() {
    if (!this.#tooltip || !this.#titleEl) return;

    const rect = this.#titleEl.getBoundingClientRect();
    const tooltipRect = this.#tooltip.getBoundingClientRect();

    let top = rect.bottom + 8;
    let left = rect.left;

    if (left + tooltipRect.width > window.innerWidth - 8) {
      left = window.innerWidth - tooltipRect.width - 8;
    }

    if (top + tooltipRect.height > window.innerHeight - 8) {
      top = rect.top - tooltipRect.height - 8;
    }

    this.#tooltip.style.top = `${top}px`;
    this.#tooltip.style.left = `${Math.max(8, left)}px`;
  }

  #hideTooltip() {
    if (this.#onDocumentClick) {
      document.removeEventListener('click', this.#onDocumentClick, true);
      this.#onDocumentClick = null;
    }

    if (this.#onKeyDown) {
      document.removeEventListener('keydown', this.#onKeyDown);
      this.#onKeyDown = null;
    }

    this.#tooltip?.remove();
    this.#tooltip = null;
  }
}

if (!customElements.get('product-title-sku-tooltip')) {
  customElements.define('product-title-sku-tooltip', ProductTitleSkuTooltip);
}

function enhanceProductTitleWithSkuTooltip() {
  document.querySelectorAll('.product-details[id*="ProductInformation-"]').forEach((details) => {
    if (!(details instanceof HTMLElement)) return;

    const title = details.querySelector('h1');
    if (!(title instanceof HTMLElement) || title.closest('product-title-sku-tooltip')) return;

    const parent = title.parentNode;
    const nextSibling = title.nextSibling;
    const wrapper = document.createElement('product-title-sku-tooltip');
    wrapper.dataset.sku = details.dataset.initialSku || '';
    wrapper.appendChild(title);
    parent?.insertBefore(wrapper, nextSibling);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', enhanceProductTitleWithSkuTooltip);
} else {
  enhanceProductTitleWithSkuTooltip();
}

document.addEventListener('shopify:section:load', enhanceProductTitleWithSkuTooltip);
